import { useEffect, useRef, useState } from 'react';
import { ChipCheckGroup, type ChipCheckGroupProps } from '~/components/new/Chip';
import DropDown, { type DropDownProps } from '~/components/new/DropDown';
// import Label from '~/components/ui/Label';
import { classNames } from '~/utils/styles';

export type ChipsAndSearchProps = Omit<ChipCheckGroupProps, 'options'> & {
    className?: string;
    title?: string;
    subtitle?: string;
    initialOptions?: LabelValueOption[];
    options?: LabelValueOption[];
    onChange?: (values: (string | number)[]) => void;
    loadOptions: (inputValue: string, options: LabelValueOption[]) => Promise<LabelValueOption[]>;
    placeholder?: string;
    'data-test-component'?: string;
    'data-test-name'?: string;
};

export default function ChipSearchBlock({
    className,
    initialOptions = [],
    options: outterOptions = [],
    values = [],
    onChange,
    loadOptions,
    placeholder,
    'data-test-component': testComponent = 'candidates/TagsBlock',
    'data-test-name': testName,
}: ChipsAndSearchProps) {
    const [options, setOptions] = useState(initialOptions);

    // combine initial options with options props.
    // initial options can be static while options might come from a stored value.
    useEffect(() => {
        if (outterOptions.length === 0) return;

        setOptions((v) => [
            ...v,
            ...outterOptions.filter((o) => !v.find((i) => i.value === o.value)),
        ]);
    }, [outterOptions]);

    const handleSelectionChange = (option: LabelValueOption) => {
        if (values.includes(option.value)) {
            onChange?.(values.filter((v) => v !== option.value));
        } else {
            onChange?.([...values, option.value]);
        }
    };

    return (
        <div
            className={classNames({
                'chips-search-block': true,
                className: Boolean(className),
            })}
            style={{ '--max-width': '100%' }}
            data-test-component={testComponent}
            data-test-name={testName}>
            <div className='flex flex-column gap-2'>
                <ChipCheckGroup
                    options={options.sort((a, b) => a.label.localeCompare(b.label))}
                    values={values}
                    onChange={(o) =>
                        o.value !== undefined ? handleSelectionChange(o as LabelValueOption) : null
                    }></ChipCheckGroup>
                <TagSearch
                    placeholder={placeholder}
                    onChange={(newOption: LabelValueOption) => {
                        // add it for selection
                        setOptions((v) => [...v, newOption]);
                        // enters selected by default
                        handleSelectionChange(newOption);
                    }}
                    loadOptions={(inputValue: string) => loadOptions(inputValue.trim(), options)}
                />
            </div>
        </div>
    );
}

export type LabelValueOption = { label: string; value: string | number };

type TagSearchProps = Omit<DropDownProps, 'options'> & {
    onChange: (value: LabelValueOption) => void;
    loadOptions?: (inputValue: string) => Promise<LabelValueOption[]>;
};

function TagSearch({ onChange, loadOptions, placeholder = 'Search...' }: TagSearchProps) {
    // making the DropDown to reset by a key prop, keeps the value empty after selection an options and always displays the placeholder
    const [resetKey, setResetKey] = useState(0);

    const handleChange = (v: LabelValueOption | LabelValueOption[]) => {
        const selectedOption = Array.isArray(v) ? v[0] : v;
        onChange(selectedOption);
        setResetKey((k) => k + 1);

        // keep focus on search after selection
        requestAnimationFrame(() => {
            selectRef.current?.focus();
        });
    };
    const selectRef = useRef<HTMLElement>(null);

    return (
        <DropDown
            key={resetKey}
            isSearchable
            size='large'
            options={[]}
            cacheOptions
            ref={selectRef}
            classNamePrefix='search-select-input'
            placeholder={placeholder}
            loadOptions={loadOptions}
            noOptionsMessage={() => null}
            onChange={handleChange}
        />
    );
}
